import { useState, useMemo, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useLocationAttendanceApiService } from "../services/api/LocationAttendanceApiService";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";
import {
  computeAttendanceCounts,
  hasShiftEnded,
} from "../utils/attendanceUtils";
import { LocationSummary, ShiftConfig } from "../services/model/ShiftLocation";
import {
  StatusFilterValue,
  VALID_STATUS_FILTERS,
} from "../utils/statusFilterConfig";

export type { StatusFilterValue };

export interface ShiftSiteDetailState {
  location: LocationSummary | undefined;
  records: EmployeeShiftRecord[];
  filteredRecords: EmployeeShiftRecord[];
  isLoading: boolean;
  error: Error | null;
  isTodayView: boolean;
  dateLabel: string;
  // Shift filter
  shiftConfigs: ShiftConfig[];
  shiftFilter: string;
  setShiftFilter: (id: string) => void;
  // Status filter
  statusFilters: StatusFilterValue[];
  setStatusFilters: (filters: StatusFilterValue[]) => void;
  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  // Stats
  onTimeCount: number;
  lateArrivalCount: number;
  noShowCount: number;
  earlyCount: number;
  noDataCount: number;
  onSiteFilteredCount: number;
  supervisorFilteredCount: number;
  adminFilteredCount: number;
  replacementCount: number;
  unscheduledCount: number;
  lateThreshold: number;
  onsiteCount: number;
  totalCount: number;
  actualHours: number;
  scheduledHours: number;
  coveragePercent: number | null;
  isMultiDay: boolean;
  dateFilter: string;
  setDateFilter: (filter: string) => void;
  availableDates: string[];
}

const isStatusFilterValue = (v: string): v is StatusFilterValue =>
  VALID_STATUS_FILTERS.has(v as StatusFilterValue);

const useShiftSiteDetail = (): ShiftSiteDetailState => {
  const { locationId } = useParams<{ locationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // State - Initialize from URL params for deep linking
  const [shiftFilter, setShiftFilterState] = useState<string>(
    searchParams.get("shift") || "all"
  );
  const [statusFilters, setStatusFiltersState] = useState<StatusFilterValue[]>(
    () => {
      const param = searchParams.get("status");
      if (!param) return [];
      return param.split(",").filter(Boolean).filter(isStatusFilterValue);
    }
  );
  const [searchQuery, setSearchQueryState] = useState<string>(
    searchParams.get("q") || ""
  );
  const [dateFilter, setDateFilterState] = useState<string>(
    searchParams.get("date") || "all"
  );

  // Wrapper setters that update URL params
  const setShiftFilter = (val: string): void => {
    setShiftFilterState(val);
    setSearchParams(
      (prev) => {
        if (val === "all") {
          prev.delete("shift");
        } else {
          prev.set("shift", val);
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setStatusFilters = (val: StatusFilterValue[]): void => {
    setStatusFiltersState(val);
    setSearchParams(
      (prev) => {
        if (val.length === 0) {
          prev.delete("status");
        } else {
          prev.set("status", val.join(","));
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setSearchQuery = (val: string): void => {
    setSearchQueryState(val);
    setSearchParams(
      (prev) => {
        if (!val.trim()) {
          prev.delete("q");
        } else {
          prev.set("q", val);
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setDateFilter = (val: string): void => {
    setDateFilterState(val);
    setSearchParams(
      (prev) => {
        if (val === "all") {
          prev.delete("date");
        } else {
          prev.set("date", val);
        }
        return prev;
      },
      { replace: true }
    );
  };

  // Date params from URL
  const today = dayjs().format("YYYY-MM-DD");
  const dateFrom = searchParams.get("date_from") || today;
  const dateTo = searchParams.get("date_to") || today;

  // View mode determination
  const isTodayView = dateFrom === today && dateTo === today;
  const isMultiDay = dateFrom !== dateTo;

  // Formatted date label
  const dateLabel = useMemo(() => {
    if (dateFrom === dateTo) {
      return dayjs(dateFrom).format("dddd, MMMM D, YYYY");
    }
    const fromDate = dayjs(dateFrom);
    const toDate = dayjs(dateTo);
    if (
      fromDate.month() === toDate.month() &&
      fromDate.year() === toDate.year()
    ) {
      return `${fromDate.format("MMMM D")} - ${toDate.format("D, YYYY")}`;
    }
    return `${fromDate.format("MMMM D, YYYY")} - ${toDate.format(
      "MMMM D, YYYY"
    )}`;
  }, [dateFrom, dateTo]);

  // Fetch data
  // Poll every 30s on the live "today" view so the per-post duty/site label
  // reflects out-of-band clock-ins and roster changes that don't invalidate
  // this query. Historical ranges never poll.
  const { data, isLoading, error } = useLocationAttendanceApiService({
    locationId: locationId || "",
    date_from: isTodayView ? undefined : dateFrom,
    date_to: isTodayView ? undefined : dateTo,
    date: isTodayView ? today : undefined,
    enabled: Boolean(locationId),
    refetchInterval: isTodayView ? 30000 : false,
  });

  const location = data?.location;
  const records = data?.records || [];
  const shiftConfigs = location?.shift_configs || [];
  const lateThreshold = location?.late_arrival_threshold_minutes ?? 0;

  // Shared shift predicate — used by both shiftRecords (stats) and filteredRecords (rows)
  const matchesShift = useCallback(
    (r: EmployeeShiftRecord): boolean =>
      shiftFilter === "all" ||
      r.shift_config_id === shiftFilter ||
      r.is_replacement === true,
    [shiftFilter]
  );

  // Records filtered by shift only (for stats - independent of status/search filters)
  const shiftRecords = useMemo(() => {
    if (shiftFilter === "all") return records;
    return records.filter(matchesShift);
  }, [records, shiftFilter, matchesShift]);

  // Per-status counts (for timeline view filter chips)
  const hasShiftEndedForRecord = useCallback(
    (r: EmployeeShiftRecord): boolean => {
      if (r.date) {
        if (dayjs(r.date).isBefore(today)) return true;
        if (dayjs(r.date).isAfter(today)) return false;
      }
      return hasShiftEnded(r.shift_start, r.shift_end);
    },
    [today]
  );

  const {
    onTimeCount,
    lateArrivalCount,
    noShowCount,
    earlyCount,
    noDataCount,
    onSiteFilteredCount,
    supervisorFilteredCount,
    adminFilteredCount,
    replacementCount,
    unscheduledCount,
  } = useMemo(() => {
    const onTime = shiftRecords.filter(
      (r) =>
        r.late_minutes !== null &&
        r.late_minutes >= 0 &&
        r.late_minutes <= lateThreshold &&
        r.status !== "not_clocked_in"
    ).length;
    const late = shiftRecords.filter(
      (r) => r.late_minutes !== null && r.late_minutes > lateThreshold
    ).length;
    const noShow = shiftRecords.filter(
      (r) =>
        r.status === "no_show" ||
        (r.status === "not_clocked_in" && hasShiftEndedForRecord(r))
    ).length;
    const early = shiftRecords.filter(
      (r) =>
        r.late_minutes !== null &&
        r.late_minutes < 0 &&
        r.status !== "not_clocked_in"
    ).length;
    const noData = shiftRecords.filter(
      (r) =>
        (r.late_minutes === null &&
          r.status !== "not_clocked_in" &&
          !r.clock_in_time) ||
        (r.status === "not_clocked_in" && !hasShiftEndedForRecord(r))
    ).length;
    const onSiteFiltered = shiftRecords.filter(
      (r) => r.clock_in_time && !r.clock_out_time
    ).length;
    const supervisorCount = shiftRecords.filter(
      (r) =>
        r.clock_in_source === "supervisor" ||
        r.clock_out_source === "supervisor"
    ).length;
    const adminCount = shiftRecords.filter(
      (r) => r.clock_in_source === "admin" || r.clock_out_source === "admin"
    ).length;
    const replacements = shiftRecords.filter(
      (r) => r.is_replacement === true
    ).length;
    const unscheduled = shiftRecords.filter(
      (r) => r.is_unscheduled === true
    ).length;
    return {
      onTimeCount: onTime,
      lateArrivalCount: late,
      noShowCount: noShow,
      earlyCount: early,
      noDataCount: noData,
      onSiteFilteredCount: onSiteFiltered,
      supervisorFilteredCount: supervisorCount,
      adminFilteredCount: adminCount,
      replacementCount: replacements,
      unscheduledCount: unscheduled,
    };
  }, [shiftRecords, hasShiftEndedForRecord, lateThreshold]);

  // Coverage stats — use shift_stats when a specific shift is selected
  const {
    onsiteCount,
    totalCount,
    actualHours,
    scheduledHours,
    coveragePercent,
  } = useMemo(() => {
    let onsite: number;
    let total: number;
    let actual: number;
    let scheduled: number;
    let coverage: number | null;

    // Coverage semantics: scheduled-only denominator. Relievers/extras attend
    // but don't occupy roster slots, so they are NOT counted in the total (that
    // off-by-one previously made a fully-covered site read X/Y+1), and the
    // on-site numerator is capped at the scheduled total so it never exceeds
    // 100%. The records-derived fallback (no backend shift_stats) uses the same
    // coverage-aware counts, so its numerator and denominator come from one
    // source rather than raw record length + open clock-ins.
    const fallback = computeAttendanceCounts(shiftRecords);

    if (shiftFilter !== "all") {
      // Specific shift — use that shift's stats, fall back to records-derived
      const shiftStat = location?.shift_stats?.[shiftFilter];
      total = shiftStat ? shiftStat.employee_count : fallback.scheduled;
      onsite = Math.min(
        shiftStat?.employees_on_site ?? fallback.covered,
        total
      );
      actual = shiftStat?.actual_hours ?? 0;
      scheduled = shiftStat?.scheduled_hours ?? 0;
      coverage = total > 0 ? Math.round((onsite / total) * 100) : 0;
    } else {
      // "All" — sum across all shift_stats, fall back to location-level / records
      const allStats = location?.shift_stats
        ? Object.values(location.shift_stats)
        : [];

      if (allStats.length > 0) {
        let rawOnsite = 0;
        total = 0;
        actual = 0;
        scheduled = 0;
        allStats.forEach((s) => {
          rawOnsite += s.employees_on_site;
          total += s.employee_count;
          actual += s.actual_hours;
          scheduled += s.scheduled_hours;
        });
        onsite = Math.min(rawOnsite, total);
        coverage = total > 0 ? Math.round((onsite / total) * 100) : 0;
      } else {
        total = fallback.scheduled;
        onsite = Math.min(
          location?.employees_on_site ?? fallback.covered,
          total
        );
        actual = location?.actual_hours ?? 0;
        scheduled = location?.scheduled_hours ?? 0;
        coverage = location?.coverage_percent ?? null;
      }
    }

    return {
      onsiteCount: onsite,
      totalCount: total,
      actualHours: actual,
      scheduledHours: scheduled,
      coveragePercent: coverage,
    };
  }, [shiftRecords, shiftFilter, location]);

  // Available dates for date filter (multi-day views)
  const availableDates = useMemo(() => {
    const dates = Array.from(
      new Set(records.map((r) => r.date).filter((d): d is string => Boolean(d)))
    ).sort();
    return dates;
  }, [records]);

  // Filter records
  const filteredRecords = useMemo(() => {
    let filtered = records;

    // Shift filter — uses shared matchesShift predicate (same as shiftRecords)
    if (shiftFilter !== "all") {
      filtered = filtered.filter(matchesShift);
    }

    // Status filter (multi-select)
    if (statusFilters.length > 0) {
      const matchesStatus = (
        r: EmployeeShiftRecord,
        status: StatusFilterValue
      ): boolean => {
        switch (status) {
          case "on_time":
            return (
              r.late_minutes !== null &&
              r.late_minutes >= 0 &&
              r.late_minutes <= lateThreshold &&
              r.status !== "not_clocked_in"
            );
          case "late_arrival":
            return r.late_minutes !== null && r.late_minutes > lateThreshold;
          case "no_show":
            return (
              r.status === "no_show" ||
              (r.status === "not_clocked_in" && hasShiftEndedForRecord(r))
            );
          case "early":
            return (
              r.late_minutes !== null &&
              r.late_minutes < 0 &&
              r.status !== "not_clocked_in"
            );
          case "no_data":
            return (
              (r.late_minutes === null &&
                r.status !== "not_clocked_in" &&
                !r.clock_in_time) ||
              (r.status === "not_clocked_in" && !hasShiftEndedForRecord(r))
            );
          case "on_site":
            return Boolean(r.clock_in_time) && !r.clock_out_time;
          case "supervisor":
            return (
              r.clock_in_source === "supervisor" ||
              r.clock_out_source === "supervisor"
            );
          case "admin":
            return (
              r.clock_in_source === "admin" || r.clock_out_source === "admin"
            );
          case "replacement":
            return r.is_replacement === true;
          case "unscheduled":
            return r.is_unscheduled === true;
          default:
            return false;
        }
      };
      filtered = filtered.filter((r) =>
        statusFilters.some((s) => matchesStatus(r, s))
      );
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((r) =>
        r.employee_name?.toLowerCase().includes(query)
      );
    }

    // Date filter (multi-day views)
    if (dateFilter !== "all") {
      filtered = filtered.filter((r) => r.date === dateFilter);
    }

    // Group replaced + replacement pairs adjacently
    // Build a map: covered_employee_id → replacement record
    const replacementByTarget = new Map<number, EmployeeShiftRecord>();
    filtered.forEach((r) => {
      if (r.is_replacement && r.covered_employee_id) {
        replacementByTarget.set(r.covered_employee_id, r);
      }
    });

    if (replacementByTarget.size > 0) {
      // Remove replacement records from their natural position —
      // they'll be re-inserted immediately after their replaced employee.
      const replacementRecords = new Set(replacementByTarget.values());
      const withoutReplacements = filtered.filter(
        (r) => !replacementRecords.has(r)
      );

      // Walk through and insert each replacement after its replaced employee
      const grouped: EmployeeShiftRecord[] = [];
      const insertedReplacements = new Set<EmployeeShiftRecord>();
      withoutReplacements.forEach((r) => {
        grouped.push(r);
        if (r.is_replaced) {
          const replacement = replacementByTarget.get(r.employee_id);
          if (replacement) {
            grouped.push(replacement);
            insertedReplacements.add(replacement);
          }
        }
      });

      // Append orphaned replacements (replaced employee was filtered out)
      replacementRecords.forEach((r) => {
        if (!insertedReplacements.has(r)) {
          grouped.push(r);
        }
      });

      return grouped;
    }

    return filtered;
  }, [
    records,
    shiftFilter,
    matchesShift,
    statusFilters,
    searchQuery,
    dateFilter,
    hasShiftEndedForRecord,
    lateThreshold,
  ]);

  return {
    location,
    records,
    filteredRecords,
    isLoading,
    error: error as Error | null,
    isTodayView,
    dateLabel,
    shiftConfigs,
    shiftFilter,
    setShiftFilter,
    statusFilters,
    setStatusFilters,
    searchQuery,
    setSearchQuery,
    onTimeCount,
    lateArrivalCount,
    noShowCount,
    earlyCount,
    noDataCount,
    onSiteFilteredCount,
    supervisorFilteredCount,
    adminFilteredCount,
    replacementCount,
    unscheduledCount,
    lateThreshold,
    onsiteCount,
    totalCount,
    actualHours,
    scheduledHours,
    coveragePercent,
    isMultiDay,
    dateFilter,
    setDateFilter,
    availableDates,
  };
};

export default useShiftSiteDetail;
